import { loadHistoryForExport } from '../utils/loadHistoryForExport';
import { useHistoryPage } from '../hooks/useHistoryPage';
import { HistoryControls } from './shared/HistoryControls';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, ProgressBar, Table } from 'react-bootstrap';
import { formatVehicleClass } from '../utils/vehicleClassFormatter';
import US_STATES, { getStateName } from '../utils/usStates';
import { formatDate } from '../utils/dateFormatter';
import { TransactionHistory } from './TransactionHistory';
import { RTOLink, CustomerLink } from './EntityLinks';
import { StatusBadge } from './shared/StatusBadge';
import { AddFinancialEntryModal } from './AddFinancialEntryModal';
import editIcon from '../assets/icons/edit.svg';
import saveIcon from '../assets/icons/save.svg';
import closeIcon from '../assets/icons/close.svg';

/**
 * VehicleDetail component displays and allows editing of a specific vehicle
 */
export function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall, user } = useOutletContext();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [stores, setStores] = useState([]);
  const [vehicleRTO, setVehicleRTO] = useState(null);
  const transactionHistory = useHistoryPage(apiCall, `/transactions?vehicle_ids=${id}`);
  const vehicleTransactions = transactionHistory.data;

  // Financial log states
  const [financialData, setFinancialData] = useState(null);
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);
  const financialLogPath = `/vehicles/${id}/financial-log?type=${filterType}&start_date=${dateRange.start}&end_date=${dateRange.end}`;
  const financialHistory = useHistoryPage(apiCall, financialLogPath);
  const filteredLog = financialHistory.data;

  useEffect(() => {
    const loadVehicle = async () => {
      try {
        // Load stores first
        const storesRes = await apiCall(`${API_BASE_URL}/stores`);
        setStores(storesRes?.data || []);

        const vehicleId = parseInt(id);
        const response = await apiCall(`${API_BASE_URL}/vehicles/${vehicleId}`);
        const foundVehicle = response.data || response;

        if (foundVehicle) {
          setVehicle(foundVehicle);
          setFormData(foundVehicle);

          // Check for active RTO agreement for this vehicle
          try {
            const rtoRes = await apiCall(`${API_BASE_URL}/rto-agreements?vehicle_id=${vehicleId}`);
            if (rtoRes?.data && rtoRes.data.length > 0) {
              setVehicleRTO(rtoRes.data[0]);
            }
          } catch (rtoErr) {
            console.error('Error loading RTO for vehicle:', rtoErr);
          }

          // Load financial data and transactions
          fetchFinancialData();
        } else {
          setError('Vehicle not found');
        }
      } catch (err) {
        setError('Failed to load vehicle: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadVehicle();
  }, [id, apiCall]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'year' || name === 'store_id' ? parseInt(value) : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await apiCall(
        `${API_BASE_URL}/vehicles/${vehicle.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(formData),
        }
      );

      if (response.success || response) {
        setVehicle(formData);
        setIsEditing(false);
        setSuccessMessage('Vehicle updated successfully');
        
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError(response.error || 'Failed to update vehicle');
      }
    } catch (err) {
      setError('Error updating vehicle: ' + err.message);
    }
  };

  const fetchFinancialData = async () => {
    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(`${API_BASE_URL}/vehicles/${id}/financial-summary?include_log=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch financial data');
      }

      const data = await response.json();
      if (data.success) {
        setFinancialData(data.data);
      }
    } catch (err) {
      console.error('Error fetching financial data:', err);
      setError(err.message);
    }
  };



  const handleEntrySuccess = async (type) => {
    const wasSold = type === 'sold';
    setSuccessMessage(wasSold ? 'Vehicle marked as sold' : 'Financial entry added successfully');
    await fetchFinancialData();
    financialHistory.reload();
    if (wasSold) {
      const response = await apiCall(`${API_BASE_URL}/vehicles/${id}`);
      if (response?.data) {
        setVehicle(response.data);
        setFormData(response.data);
      }
    }
  };

  const handleDeleteEntry = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(`${API_BASE_URL}/vehicles/${id}/car-costs/${costId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete entry');
      }

      setSuccessMessage('Entry deleted successfully');
      await fetchFinancialData();
      financialHistory.reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
    if (!financialData) return;

    const headers = ['Date', 'Type', 'Description', 'Notes', 'Agent', 'Amount', 'Running Balance'];
    const allEntries = await loadHistoryForExport(apiCall, financialLogPath);
    const rows = allEntries.map(entry => [
      entry.entry_date,
      entry.entry_type,
      entry.description,
      entry.notes || '',
      entry.agent_name || 'N/A',
      entry.amount.toFixed(2),
      entry.running_balance.toFixed(2),
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vehicle_${id}_financial_log_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.message || 'Could not export financial history'); }
    finally { setExporting(false); }
  };

  const canAddEntry = user?.role === 'manager' || user?.role === 'admin';

  if (loading) {
    return <div className="loading">Loading vehicle details...</div>;
  }

  if (error && !vehicle) {
    return (
      <section className="vehicle-detail">
        <button onClick={() => navigate('/dashboard/vehicles')} className="back-btn">
          ← Back to Vehicles
        </button>
        <div className="error-message">{error}</div>
      </section>
    );
  }

  return (
    <Container fluid className="py-4 domain-vehicles">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/vehicles')} className="p-0">
            ← Back to Vehicles
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h2>
            {vehicle.year} {vehicle.make} {vehicle.model}
            <span style={{ fontSize: '0.8em', marginLeft: '10px', color: '#666' }}>
              (ID: {vehicle.id})
            </span>
          </h2>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {!isEditing ? (
        <Card>
          <Card.Header>
            <h3>Vehicle Information</h3>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Make</strong>
                <span>{vehicle.make}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Model</strong>
                <span>{vehicle.model}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Year</strong>
                <span>{vehicle.year}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Color</strong>
                <span>{vehicle.color}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>VIN</strong>
                <span>{vehicle.vin}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>License Plate</strong>
                <span>{vehicle.license_plate} ({vehicle.license_plate_state || '-'})</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>State</strong>
                <span>{vehicle.license_plate_state ? getStateName(vehicle.license_plate_state) : '-'}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Class</strong>
                <span>{formatVehicleClass(vehicle.class)}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Store</strong>
                <span>{stores.find(s => s.id === vehicle.store_id)?.name || `Store ${vehicle.store_id}`}</span>
              </Col>
            </Row>
            <div className="mt-3">
              {user?.role !== 'auditor' && (
                <>
                  <Button
                    variant="primary"
                    onClick={() => setIsEditing(true)}
                    className="me-2"
                  >
                    <img src={editIcon} alt="" className="icon-btn" />
                    Edit Vehicle
                  </Button>
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h3>Edit Vehicle</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSave}>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Make</Form.Label>
                    <Form.Control
                      type="text"
                      name="make"
                      value={formData.make}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Model</Form.Label>
                    <Form.Control
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Year</Form.Label>
                    <Form.Control
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <Form.Control
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>VIN</Form.Label>
                    <Form.Control
                      type="text"
                      name="vin"
                      value={formData.vin}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>License Plate</Form.Label>
                    <Form.Control
                      type="text"
                      name="license_plate"
                      value={formData.license_plate}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>License Plate State *</Form.Label>
                    <Form.Select
                      name="license_plate_state"
                      value={formData.license_plate_state || 'IN'}
                      onChange={handleFormChange}
                      required
                    >
                      {US_STATES.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.code} - {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Class</Form.Label>
                    <Form.Select
                      name="class"
                      value={formData.class}
                      onChange={handleFormChange}
                    >
                      <option value="basic_economy">Basic Economy</option>
                      <option value="full_size_sedan">Full Size Sedan</option>
                      <option value="newer_sedan_suv">Newer Sedan/SUV</option>
                      <option value="luxury">Luxury</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Store</Form.Label>
                    <Form.Select name="store_id" value={formData.store_id} onChange={handleFormChange}>
                      <option value="">Select a store</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>
                          {store.name} {store.city ? `(${store.city})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="mt-3">
                <Button type="submit" variant="primary" className="me-2">
                  <img src={saveIcon} alt="" className="icon-btn" />
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData(vehicle);
                    setError(null);
                  }}
                >
                  <img src={closeIcon} alt="" className="icon-btn" />
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* RTO Agreement Section */}
      {vehicleRTO && (
        <Card className="mb-4">
          <Card.Header style={{ backgroundColor: '#ccfbf1' }}>
            <h3>
              <Badge bg="warning" text="dark" className="me-2">LEASE</Badge>
              Active Long Term Lease Agreement
            </h3>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Agreement ID</strong>
                <RTOLink rtoId={vehicleRTO.id}>
                  #{vehicleRTO.id}
                </RTOLink>
              </Col>
              <Col md={4} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Customer</strong>
                <CustomerLink
                  customerId={vehicleRTO.customer_id}
                  customerName={vehicleRTO.customer_name}
                />
              </Col>
              <Col md={4} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Status</strong>
                <StatusBadge status={vehicleRTO.status} type="rto" delinquencyStatus={vehicleRTO.delinquency_status} />
              </Col>
              <Col md={4} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Total Sale Price</strong>
                <span>${parseFloat(vehicleRTO.total_sale_price).toLocaleString()}</span>
              </Col>
              <Col md={4} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Balance Remaining</strong>
                <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                  ${parseFloat(vehicleRTO.balance_remaining).toLocaleString()}
                </span>
              </Col>
              <Col md={4} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Next Payment Due</strong>
                <span>{vehicleRTO.next_payment_due_date?.split('T')[0]}</span>
              </Col>
            </Row>
            <Button
              variant="warning"
              onClick={() => navigate(`/dashboard/rto/${vehicleRTO.id}`)}
            >
              View Lease Details
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Financial Summary Cards */}
      {financialData && financialData.financial_summary && (
        <>
          <Row className="mb-4">
            {financialData.financial_summary.sale_profit !== null ? (
              <>
                <Col>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-muted small">CAPITAL ASSET</Card.Title>
                      <h4>${financialData.financial_summary.purchase_cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h4>
                      <small className="text-muted">Purchase Cost</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-muted small">OPERATING EXPENSES</Card.Title>
                      <h4 className="text-danger">
                        ${financialData.financial_summary.total_operating_expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">
                        R: ${financialData.financial_summary.total_repairs.toFixed(2)} |
                        M: ${financialData.financial_summary.total_maintenance.toFixed(2)} |
                        A: ${financialData.financial_summary.total_adjustments.toFixed(2)}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-muted small">REVENUE</Card.Title>
                      <h4 className="text-success">
                        ${financialData.financial_summary.rental_revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">Total Rental Revenue</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card className={financialData.financial_summary.profit_loss >= 0 ? 'border-success' : 'border-danger'}>
                    <Card.Body>
                      <Card.Title className="text-muted small">PROFIT/LOSS</Card.Title>
                      <h4 className={financialData.financial_summary.profit_loss >= 0 ? 'text-success' : 'text-danger'}>
                        ${financialData.financial_summary.profit_loss.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">
                        ROI: {financialData.financial_summary.roi_percent.toFixed(1)}%
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card style={{ borderLeft: `4px solid ${financialData.financial_summary.sale_profit >= 0 ? '#198754' : '#dc3545'}` }}>
                    <Card.Body>
                      <Card.Title className="text-muted small">SALE PROFIT/LOSS</Card.Title>
                      <h4 className={financialData.financial_summary.sale_profit >= 0 ? 'text-success' : 'text-danger'}>
                        ${financialData.financial_summary.sale_profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">
                        Sale - Cost - Expenses
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </>
            ) : (
              <>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-muted small">CAPITAL ASSET</Card.Title>
                      <h4>${financialData.financial_summary.purchase_cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h4>
                      <small className="text-muted">Purchase Cost</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-muted small">OPERATING EXPENSES</Card.Title>
                      <h4 className="text-danger">
                        ${financialData.financial_summary.total_operating_expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">
                        Repairs: ${financialData.financial_summary.total_repairs.toFixed(2)} |
                        Maintenance: ${financialData.financial_summary.total_maintenance.toFixed(2)} |
                        Adjustments: ${financialData.financial_summary.total_adjustments.toFixed(2)}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-muted small">REVENUE</Card.Title>
                      <h4 className="text-success">
                        ${financialData.financial_summary.rental_revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">Total Rental Revenue</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className={financialData.financial_summary.profit_loss >= 0 ? 'border-success' : 'border-danger'}>
                    <Card.Body>
                      <Card.Title className="text-muted small">PROFIT/LOSS</Card.Title>
                      <h4 className={financialData.financial_summary.profit_loss >= 0 ? 'text-success' : 'text-danger'}>
                        ${financialData.financial_summary.profit_loss.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </h4>
                      <small className="text-muted">
                        ROI: {financialData.financial_summary.roi_percent.toFixed(1)}%
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </>
            )}
          </Row>

          {/* RTO Payment Details - only shown if vehicle has an RTO */}
          {vehicleRTO && (
            <Row className="mb-4">
              <Col md={3}>
                <Card style={{ borderLeft: '4px solid #ffc107' }}>
                  <Card.Body>
                    <Card.Title className="text-muted small">
                      <Badge bg="warning" text="dark" className="me-2">LEASE</Badge>
                      AGREEMENT AMOUNT
                    </Card.Title>
                    <h4>${parseFloat(vehicleRTO.total_sale_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</h4>
                    <RTOLink rtoId={vehicleRTO.id} className="btn btn-outline-warning btn-sm">
                      View Agreement #{vehicleRTO.id}
                    </RTOLink>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card style={{ borderLeft: '4px solid #ffc107' }}>
                  <Card.Body>
                    <Card.Title className="text-muted small">LEASE START DATE</Card.Title>
                    <h4>{formatDate(vehicleRTO.start_date)}</h4>
                    <small className="text-muted">Agreement Start</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card style={{ borderLeft: '4px solid #ffc107' }}>
                  <Card.Body>
                    <Card.Title className="text-muted small">LEASE MATURATION DATE</Card.Title>
                    <h4>
                      {(() => {
                        const balanceRemaining = parseFloat(vehicleRTO.balance_remaining) || 0;
                        const recurringAmount = parseFloat(vehicleRTO.recurring_amount) || 0;

                        if (balanceRemaining <= 0) {
                          return 'Paid Off';
                        }
                        if (recurringAmount <= 0) {
                          return 'TBD';
                        }

                        const paymentsRemaining = Math.ceil(balanceRemaining / recurringAmount);
                        const today = new Date();
                        let estimatedDate = new Date(today);

                        if (vehicleRTO.payment_frequency === 'weekly') {
                          estimatedDate.setDate(today.getDate() + (paymentsRemaining * 7));
                        } else if (vehicleRTO.payment_frequency === 'biweekly') {
                          estimatedDate.setDate(today.getDate() + (paymentsRemaining * 14));
                        } else {
                          estimatedDate.setMonth(today.getMonth() + paymentsRemaining);
                        }

                        return formatDate(estimatedDate.toISOString());
                      })()}
                    </h4>
                    <small className="text-muted">
                      {parseFloat(vehicleRTO.balance_remaining) > 0 ? 'Estimated Payoff' : 'Completed'}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card style={{ borderLeft: '4px solid #ffc107' }}>
                  <Card.Body>
                    <Card.Title className="text-muted small">LEASE RATE</Card.Title>
                    <h4>${parseFloat(vehicleRTO.recurring_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</h4>
                    <small className="text-muted">
                      Per {vehicleRTO.payment_frequency === 'weekly' ? 'Week' :
                           vehicleRTO.payment_frequency === 'biweekly' ? '2 Weeks' : 'Month'}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Breakeven Progress */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5>Breakeven Progress</h5>
                    <Badge bg={financialData.financial_summary.breakeven_percent >= 100 ? 'success' : 'warning'}>
                      {financialData.financial_summary.breakeven_status}
                    </Badge>
                  </div>
                  <ProgressBar
                    now={Math.min(financialData.financial_summary.breakeven_percent, 100)}
                    label={`${financialData.financial_summary.breakeven_percent.toFixed(1)}%`}
                    variant={financialData.financial_summary.breakeven_percent >= 100 ? 'success' : 'info'}
                    style={{ height: '30px', fontSize: '16px' }}
                  />
                  <small className="text-muted mt-2 d-block">
                    Revenue: ${financialData.financial_summary.rental_revenue.toFixed(2)} / Purchase Cost: ${financialData.financial_summary.purchase_cost.toFixed(2)}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Actions and Filters */}
          <Row className="mb-3">
            <Col md={8}>
              <div className="d-flex gap-3 align-items-center">
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ width: '200px', fontSize: '0.7rem'}}
                >
                  <option value="all">All Types</option>
                  <option value="purchase_cost">Purchase Cost</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="repair">Repair</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="depreciation">Depreciation</option>
                  <option value="sold">Sold</option>
                  <option value="rental">Rental Revenue</option>
                </Form.Select>

                <Form.Control
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  placeholder="Start Date"
                  style={{ width: '180px', fontSize: '0.7rem' }}
                />
                <span>to</span>
                <Form.Control
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  placeholder="End Date"
                  style={{ width: '180px', fontSize: '0.7rem' }}
                />

                {(dateRange.start || dateRange.end) && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setDateRange({ start: '', end: '' })}
                  >
                    Clear Dates
                  </Button>
                )}
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button variant="outline-primary" onClick={exportToCSV} disabled={exporting} className="me-2">
                Export CSV
              </Button>
              {canAddEntry && (
                <Button variant="primary" onClick={() => setShowAddEntryModal(true)}>
                  Add Entry
                </Button>
              )}
            </Col>
          </Row>

          {/* Transaction History Section */}
          <Row className="mb-4">
            <Col>
              <HistoryControls history={transactionHistory} label="transactions" />
              <TransactionHistory transactions={vehicleTransactions} customers={[]} payments={[]} />
            </Col>
          </Row>

          {/* Financial Log Table */}
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Financial Log ({financialHistory.total || 0} entries)</h5>
                  <HistoryControls history={financialHistory} label="financial entries" />
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table striped hover responsive>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Agent</th>
                          <th className="text-end">Amount</th>
                          <th className="text-end">Running Balance</th>
                          {user?.role === 'admin' && <th className="text-center">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLog.length === 0 ? (
                          <tr>
                            <td colSpan={user?.role === 'admin' ? 7 : 6} className="text-center text-muted">
                              No entries found
                            </td>
                          </tr>
                        ) : (
                          filteredLog.map((entry, index) => (
                            <tr key={`${entry.source}-${entry.id}-${index}`}>
                              <td>{formatDate(entry.entry_date)}</td>
                              <td>
                                <Badge bg={
                                  entry.entry_type === 'rental' ? 'success' :
                                  entry.entry_type === 'purchase_cost' ? 'dark' :
                                  entry.entry_type === 'adjustment' ? 'warning' :
                                  entry.entry_type === 'depreciation' ? 'secondary' :
                                  entry.entry_type === 'sold' ? 'primary' :
                                  'info'
                                }>
                                  {entry.entry_type.replace('_', ' ').split(' ').map(word =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                  ).join(' ')}
                                </Badge>
                              </td>
                              <td>
                                {entry.description}
                                {entry.notes && (
                                  <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                    {entry.notes}
                                  </div>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">{entry.agent_name || 'N/A'}</small>
                              </td>
                              <td className={`text-end ${entry.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                                {entry.amount >= 0 ? '+' : ''}${entry.amount.toFixed(2)}
                              </td>
                              <td className={`text-end fw-bold ${entry.running_balance >= 0 ? 'text-success' : 'text-danger'}`}>
                                ${entry.running_balance.toFixed(2)}
                              </td>
                              {user?.role === 'admin' && (
                                <td className="text-center">
                                  {entry.source === 'cost' && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteEntry(entry.id)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <AddFinancialEntryModal
            show={showAddEntryModal}
            onHide={() => setShowAddEntryModal(false)}
            onSuccess={handleEntrySuccess}
            apiCall={apiCall}
            userRole={user?.role}
            vehicleId={id}
            vehicleStatus={vehicle?.status}
          />
        </>
      )}
    </Container>
  );
}

export default VehicleDetail;
